import { SiteUrls } from '@tableau/shared-urls';

import { enterSpace } from '../../app/actions/spaces/EnterSpaceAction';
import { appStore } from '../../app/AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs,
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { DevicePreviewModalBuilder } from '../../app/content/devicePreview/DevicePreviewModalBuilder';
import { VizViewerWidgetsBar } from '../../app/content/VizViewerWidgetsBar';
import { selectLayoutType } from '../../app/selectors/LayoutTypeSelector';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { RequestAccessErrorProps } from '../../app/spaces/RouterHelpers/RequestAccessError';
import { RouterErrorOwnProps } from '../../app/spaces/RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../../app/spaces/SpaceTelemetry';
import { VizSpaceProviderState } from '../../app/spaces/VizSpace/VizSpaceProvider';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { DeprecatedEventWrapper } from '../../common/analyticsEvents/DeprecatedEventWrapper';
import { FavoriteToggle } from '../../common/react/components/FavoriteToggle/FavoriteToggle';
import { DevicePreviewShowPreviewEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { isCurrentUrlViz } from '../helpers/UrlHelpers';
import { ViewDefinition } from '../models/ContentDefinitions';
import { ViewContentType } from '../models/ContentTypes';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { IViewBase, IViewDetailInfo } from '../models/IViewInfo';
import { IExplorableItemTypeInfo, INamedItemInfo } from '../models/resourceInfoParts';
import { IViz, VizType } from '../models/Viz';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { DataSources } from '../resources/Datasources';
import { Favorites } from '../resources/Favorites';
import { Projects } from '../resources/Projects';
import { Views } from '../resources/Views';
import { IViewActions } from '../ServerApi/types/IViewActions';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ContentActions } from '../services/ContentActions';
import { DeprecatedAnalyticsService } from '../services/DeprecatedAnalyticsService';
import { DevicePreviewService } from '../services/DevicePreviewService';
import { ServerService } from '../services/ServerService';
import { TelemetryService } from '../services/TelemetryService';
import { VizService } from '../services/VizService';

export interface IVizProperties {
  view?: IViewBase;
}

export interface IVizViewerStateParams extends ng.ui.IStateParamsService {
  path: string;
}

export interface IVizViewerScope extends ng.IScope {
  viz: IViz;
  vizProps: IVizProperties;
  favoriteToggleProps: FavoriteToggle.Props;
  vizViewerWidgetsBarProps: VizViewerWidgetsBar.Props;
  hasError: () => boolean;
  hasViewInfo: () => boolean;
  onVizSized: () => void;
  vizInitialized: boolean;
  shouldShowWhoHasSeenThisView: () => boolean;
  allowedAction: IViewActions;

  // used to display a RouterError
  // for the 'dont-have-access' errors
  displayRoutingError: boolean;
  displayDataAlertRoutingError: boolean;
  routerErrorProps: RouterErrorOwnProps;  // delete with vizRR
  requestAccessEnabled: boolean;  // delete with vizRR
  requestAccessErrorProps: RequestAccessErrorProps;  // delete with vizRR

  deprecatedEventFactory: DeprecatedEventWrapper;

  updateVizProviderState: (newState: Pick<VizSpaceProviderState, keyof VizSpaceProviderState>) => void;
}

// TODO remove with vizRR
export class VizViewerCtrl {

  public static $inject = [
    '$scope',
    '$q',
    '$stateParams',
    'BrowserTitleService',
    'ContentActions',
    'Projects',
    'Views',
    'DataAlerts',
    'DataSources',
    'ServerService',
    'VizService',
    'DeprecatedAnalyticsService',
    'Favorites',
    '$location',
    'DevicePreviewService',
    'AngularContextFactory',
    'TelemetryService',
  ];

  private hasError: boolean;
  private hasViewError: boolean = false;
  private deprecatedEventFactory: DeprecatedEventWrapper;

  constructor(
    private $scope: IVizViewerScope,
    private $q: ng.IQService,
            $stateParams: IVizViewerStateParams,
    private BrowserTitleService: BrowserTitleService,
    private contentActions: ContentActions,
    private Projects: Projects,
    private views: Views,
            dataAlerts: DataAlerts,
            dataSources: DataSources,
    private server: ServerService,
            vizService: VizService,
    private deprecatedAnalytics: DeprecatedAnalyticsService,
    private Favorites: Favorites,
            $location: ng.ILocationService,
    private devicePreviewService: DevicePreviewService,
            angularContextFactory: AngularContextFactory,
    private telemetryService: TelemetryService,
  ) {
    // nullify the space info item until we fetch the viz info
    appStore.dispatch(enterSpace(
      {
        item: undefined,
        spaceType:  ViewDefinition.contentType,
        subspaceName: undefined
      }
    ));
    $scope.displayDataAlertRoutingError = false;
    $scope.displayRoutingError = false;
    $scope.requestAccessEnabled = this.server.isRequestAccessEnabled();

    this.deprecatedEventFactory = this.deprecatedAnalytics.EventFactory(DeprecatedEventWrapper.eventTypes.VisitAlertFromEmail);
    // Initial viz state
    $scope.viz = {
      path: $stateParams.path,
      vizType: VizType.View,
      params: vizService.getVizParams(),
      siteUrlName: server.sessionSiteUrlName(),
      instanceId: vizService.getInstanceId(),
      isExplicitOriginalViz: vizService.getIsExplicitOriginalViz()
    };

    $scope.onVizSized = () => $scope.vizInitialized = true;

    $scope.vizProps = {};

    const siteUrls = new SpacesSiteUrls({siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search});
    const handleDevicePreviewClick = () => {
      this.telemetryService.sendEvent<DevicePreviewShowPreviewEvent>({
        type: TelemetryEvents.DevicePreviewShowPreview
      });
      const angularContext = angularContextFactory.create($scope);
      DevicePreviewModalBuilder.openPreview(angularContext, $scope.viz.path);
    };

    $scope.favoriteToggleProps = {
      isFavorite: false,
      onAddFavorite: () => Favorites.addView($scope.vizProps.view),
      onRemoveFavorite: () => Favorites.removeView($scope.vizProps.view),
      canHaveKeyboardFocus: true
    };

    $scope.vizViewerWidgetsBarProps = {
      view: null,
      siteUrls: siteUrls,
      dataSources: dataSources,
      showDevicePreview: false,
      handleDevicePreviewClick: handleDevicePreviewClick,
      layoutType: selectLayoutType(appStore.getState()),
      telemetryService: this.telemetryService
    };

    function redirectToGetByPathError(errorCode: Number) {
      $scope.displayRoutingError = true;
      $scope.routerErrorProps = {
        urls: siteUrls,
        errorType: 'view'
      };

      $scope.requestAccessErrorProps = {
        contentIdentifier: $scope.viz.path,
        contentType: 'view',
        errorCode: errorCode,
        url: siteUrls.exploreUrl()
      };
    }

    const parentThis = this;
    $scope.$watch('viz.path', (newPath: string) => {

      if(!newPath) {
        redirectToGetByPathError(CommonErrorCode.UnexpectedParameterValue);
        return;
      }

      vizService.updateVizPath($scope, newPath);

      parentThis.hasError = false;
      views.getByPath(newPath)
        .then((viewInfo: IViewDetailInfo) => {
          this.loadNewViewInfo(viewInfo);

          // because the viewInfo can return after we have navigated away from this page, we only want to call
          // enterSpace if we are still on the view page. Otherwise, all sorts of errors happen if the SpaceInfo
          // is wrong- TFSID 956506
          if (isCurrentUrlViz()) {
            appStore.dispatch(enterSpace(
              {
                item: viewInfo,
                spaceType: ViewDefinition.contentType,
                subspaceName: undefined
              }
            ));
          }

          //if fromEmail param in location.search then log the event
          if($location.search()[UrlParamKeys.FromFailureEmail]) {
              // Deprecated, use TelemetryService instead of EventFactory.
              this.deprecatedEventFactory.sendFromEmailEvent(
                  DeprecatedEventWrapper.eventTypes.VisitAlertFromEmail,
                  'Failure',
                  alertId,
                  viewInfo.id,
                  viewInfo.workbook.id,
                  this.server.sessionSiteRole());
          }
          if($location.search()[UrlParamKeys.FromSuccessEmail]) {
              // Deprecated, see TelemetryService instead.
              this.deprecatedEventFactory.sendFromEmailEvent(
                  DeprecatedEventWrapper.eventTypes.VisitAlertFromEmail,
                  'Success',
                  alertId,
                  viewInfo.id,
                  viewInfo.workbook.id,
                  this.server.sessionSiteRole());
          }

          maybeSendTelemetry_favoriteItemOpened(this.telemetryService, viewInfo);
        })
        .catch((errorCode: number) => {
          parentThis.hasViewError = true;

          // B153202, TFSID: 463801 - If user does not have permission to view the viz but guest does
          // then we recieve a GuestPermittedViewNotFoundError, so we can try to automatically
          // redirect to to the embedded view (if not a site or server admin). But if there's a regular
          // ViewNotFound error, there's no chance for guest to be able to view it, so just show the error here and now.
          if (errorCode === CommonErrorCode.GuestPermittedViewNotFound && server.isGuestEnabled() && !server.isSessionSiteOrServerAdmin()) {
            vizService.redirectToEmbeddedMode(false);
          } else {
            redirectToGetByPathError(errorCode);
          }
        });

      //Check for errors in alertId part
      const alertId = $location.search()[UrlParamKeys.EditDataAlert];
      const siteUrls = new SpacesSiteUrls({siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search});

      //Using siteUrls.viewUrl($location.path()) would preserve query string, which won't work
      //So we're explicitly getting the view URL and explicity manually adding the hash
      const viewUrl = siteUrls.viewUrl($stateParams.path);

      if(alertId) {
        dataAlerts.getById(alertId)
          .then((dataAlert: IDataAlertInfo) => {
            //we don't want an alert error if there's a view error
            if (parentThis.hasViewError) {
              return;
            }

            if (!dataAlert) {
              this.$scope.routerErrorProps = {
                errorType: 'view-data-alert-exists',
                urls: siteUrls,
                dataAlertErrorData: {
                  viewUrl: viewUrl,
                  owner: undefined
                }
              };
              this.$scope.displayDataAlertRoutingError = true;
              this.$scope.displayRoutingError = true;

            } else if (dataAlert.owner.id !== this.server.sessionUserId() && !server.isSessionSiteOrServerAdmin()) {
              this.$scope.routerErrorProps = {
                errorType: 'view-data-alert-permissions',
                urls: siteUrls,
                dataAlertErrorData: {
                  viewUrl: viewUrl,
                  owner: dataAlert.owner
                }
              };
              this.$scope.displayDataAlertRoutingError = true;
              this.$scope.displayRoutingError = true;
            } else if (this.server.isViewerUser()) {
              this.$scope.routerErrorProps = {
                errorType: 'view-data-alert-viewer',
                urls: siteUrls,
                dataAlertErrorData: {
                  viewUrl: viewUrl,
                  owner: undefined
                }
              };
              this.$scope.displayDataAlertRoutingError = true;
              this.$scope.displayRoutingError = true;
            }
          })
          .catch((errorCode: number) => {
            this.$scope.routerErrorProps = {
              errorType: 'view-data-alert-exists',
              urls: siteUrls,
              dataAlertErrorData: {
                viewUrl: viewUrl,
                owner: undefined
              }
            };
            this.$scope.displayDataAlertRoutingError = true;
            this.$scope.displayRoutingError = true;
          });
      }
    });

    $scope.$watch('viz.instanceId', (newInstanceId: string) => {
      if (newInstanceId) {
        vizService.updateInstanceId($scope, newInstanceId);
      }
    });

    $scope.$watch('viz.isExplicitOriginalViz', (isExplicitOriginalViz: boolean) => {
      vizService.updateExplicitOriginalViz($scope, isExplicitOriginalViz);
    });

    vizService.reloadOnLocationChange($scope);
  }

  private loadNewViewInfo(viewInfo: IViewDetailInfo): ng.IPromise<any> {

    return this.Projects.getProjectHierarchy(viewInfo.workbook.project.id).then((projectHierarchy: INamedItemInfo[]) => {
      this.BrowserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
      this.$scope.vizProps.view = viewInfo;

      // Update breadcrumbs
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      const siteUrls = siteUrlsSelector(appStore.getState());
      const workbook: IExplorableItemTypeInfo<'workbook'> = {
        id: viewInfo.workbook.id,
        name: viewInfo.workbook.name,
        contentType: 'workbook'
      };

      // because the project hierarchy can return after we have navigated away from this page, we only want to call
      // populateContentBreadcrumbs if we are still on the view page. Otherwise, the wrong breadcrumbs show.
      // TFSID 956506
      if (isCurrentUrlViz()) {
        populateContentBreadcrumbs([...projectHierarchyWithType, workbook, viewInfo], siteUrls);
      }

      // Update favorite toggle
      this.$scope.favoriteToggleProps.onAddFavorite = () => this.Favorites.addView(viewInfo);
      this.$scope.favoriteToggleProps.onRemoveFavorite = () => this.Favorites.removeView(viewInfo);
      this.$scope.favoriteToggleProps.isFavorite = viewInfo.favorite;

      this.views.markRecentlyViewed(viewInfo.id);

      return this.views.actions(viewInfo.id).then((actions: IViewActions) => {
          this.$scope.allowedAction = actions;
      });
    });
  }

}

Module.instance().controller('VizViewerCtrl', VizViewerCtrl);
